import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import { useWorkspaceStore } from "../store/workspace/WorkspaceStore";
import { useMatchDataProcessingStore } from "../store/MatchDataProcessingStore";
import { CompanyMatchPollingManager } from "./CompanyMatchPollingManager";
import { WorkspaceVisibility } from "../types/workspace";

export const WorkspaceLoader: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { id } = useParams<{ id: string }>();
  const fetchWorkspace = useWorkspaceStore((state) => state.fetchWorkspace);
  const isLoading = useWorkspaceStore((state) => state.isLoading);
  const isSaving = useWorkspaceStore((state) => state.isSaving);
  const hasUnsavedChanges = useWorkspaceStore(
    (state) => state.hasUnsavedChanges
  );
  const workspace = useWorkspaceStore((state) => state.draft);
  const currentDraftWorkspaceId = useWorkspaceStore((state) => state.draft?.id);
  const [initialLoading, setInitialLoading] = useState(true);
  const userCanSave = workspace?.permissions.can_write || false;
  const loadFromLocalStorage = useMatchDataProcessingStore(
    (state) => state.loadFromLocalStorage
  );

  useEffect(() => {
    if (id) {
      if (id !== currentDraftWorkspaceId) {
        setInitialLoading(true);
        fetchWorkspace(id).finally(() => {
          setInitialLoading(false);
        });
      } else {
        setInitialLoading(false);
      }
    } else {
      setInitialLoading(false);
    }
  }, [id, fetchWorkspace, currentDraftWorkspaceId]);

  // Load processing files when workspace actually changes
  useEffect(() => {
    if (currentDraftWorkspaceId) {
      loadFromLocalStorage(currentDraftWorkspaceId);
    }
  }, [currentDraftWorkspaceId, loadFromLocalStorage]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const isBlocked =
        hasUnsavedChanges &&
        (userCanSave || workspace?.visibility === WorkspaceVisibility.PUBLIC);
      if (isBlocked) {
        // The standard message will be shown by the browser
        // (browsers don't allow custom messages anymore for security reasons)
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, userCanSave, workspace?.visibility]);

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(4px)",
        }}
        open={initialLoading || (isLoading && !workspace) || isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* Polling manager with key to force re-mount when workspace changes */}
      {currentDraftWorkspaceId && (
        <CompanyMatchPollingManager key={currentDraftWorkspaceId} />
      )}
      {children}
    </>
  );
};
